import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MapPin, Lock, ArrowLeft, Compass, Loader2, Lightbulb, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TreasureHunt() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/treasure`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setData({ is_live: false, clues: [] }); setLoading(false); });
  }, []);

  const active = data?.clues?.find(c => c.id === activeId);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1307] via-[#0e0906] to-black text-white relative overflow-x-hidden">
      {/* Parchment texture overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' /></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.4'/></svg>\")",
        }}
      />

      {/* Top nav */}
      <div className="relative z-10 flex items-center justify-between px-5 py-4 border-b border-yellow-900/30">
        <Link to="/" className="flex items-center gap-2 text-yellow-400/80 hover:text-yellow-300 font-heading text-xs tracking-widest uppercase">
          <ArrowLeft size={14} /> back to okiescape
        </Link>
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-yellow-400" fill="rgba(250,204,21,0.3)" />
          <span className="font-heading text-[11px] tracking-widest uppercase text-yellow-400/80">light found · treasure unlocked</span>
        </div>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 size={48} className="text-yellow-400 animate-spin mb-3" />
          <p className="font-heading text-sm tracking-widest uppercase text-yellow-400/70">Unrolling the map...</p>
        </div>
      )}

      {!loading && !data?.is_live && (
        <div className="max-w-2xl mx-auto px-5 py-20 text-center">
          <Lock size={56} className="text-yellow-400/60 mx-auto mb-5" />
          <h1 className="font-heading text-3xl sm:text-4xl tracking-wide uppercase text-yellow-200 mb-3">The Map is Rolled Up</h1>
          <p className="font-sans text-sm text-yellow-200/70">The treasure hunt is currently paused. Check back soon — a new trail is being laid.</p>
        </div>
      )}

      {!loading && data?.is_live && (
        <div className="max-w-5xl mx-auto px-5 py-10 relative z-10">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-yellow-400/80 font-heading text-[11px] tracking-widest uppercase mb-3">
              <Compass size={14} /> Geocache Treasure Hunt
            </div>
            <h1 className="font-heading text-4xl sm:text-6xl tracking-wide uppercase mb-3" style={{
              color: '#fde68a',
              textShadow: '0 0 22px rgba(253,224,71,0.3), 0 2px 8px rgba(0,0,0,0.8)',
              fontFamily: '"Bebas Neue","Oswald",serif',
            }}>
              {data.title || 'Okinawa Treasure Hunt'}
            </h1>
            <p className="font-sans text-base text-yellow-100/80 max-w-2xl mx-auto">{data.subtitle}</p>
            {data.intro && (
              <p className="font-sans text-sm text-yellow-100/60 mt-4 max-w-2xl mx-auto leading-relaxed italic">{data.intro}</p>
            )}
          </div>

          {/* Empty state */}
          {data.clues.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed border-yellow-900/50 rounded-2xl bg-black/30">
              <MapPin size={44} className="text-yellow-400/60 mx-auto mb-3" />
              <p className="font-heading text-sm tracking-widest uppercase text-yellow-200/70">No clues on the map yet</p>
              <p className="font-sans text-xs text-yellow-200/50 mt-1">Check back soon — the hunt is being prepared.</p>
            </div>
          )}

          {/* Clues list */}
          <div className="space-y-5">
            {data.clues.map((c, i) => (
              <TreasureClueCard
                key={c.id}
                index={i + 1}
                clue={c}
                onOpen={() => setActiveId(c.id)}
              />
            ))}
          </div>

          {/* Footer */}
          {data.footer && (
            <div className="mt-12 pt-6 border-t border-yellow-900/30 text-center">
              <p className="font-heading text-xs tracking-widest uppercase text-yellow-400/80">{data.footer}</p>
            </div>
          )}
        </div>
      )}

      {/* Clue detail modal */}
      {active && (
        <ClueModal
          clue={active}
          clueNumber={(data?.clues || []).findIndex(c => c.id === active.id) + 1}
          hintContact={data?.hint_contact || ''}
          onClose={() => setActiveId(null)}
        />
      )}
    </div>
  );
}

function TreasureClueCard({ index, clue, onOpen }) {
  const locked = !clue.unlocked;
  return (
    <button
      onClick={locked ? undefined : onOpen}
      disabled={locked}
      data-testid={`clue-card-${clue.id}`}
      className={`w-full text-left group flex gap-4 rounded-2xl overflow-hidden border transition-all ${locked ? 'border-yellow-900/30 bg-black/40 cursor-not-allowed' : 'border-yellow-700/40 bg-gradient-to-br from-[#1a1307]/80 to-black/70 hover:border-yellow-500 hover:shadow-[0_0_30px_rgba(250,204,21,0.15)]'}`}
      style={{ backdropFilter: 'blur(4px)' }}
    >
      {/* Numbered seal */}
      <div className="w-20 sm:w-28 shrink-0 flex flex-col items-center justify-center relative py-6 border-r border-yellow-900/40"
        style={{ background: 'radial-gradient(ellipse at center, rgba(250,204,21,0.15), transparent 70%)' }}
      >
        {locked ? (
          <Lock size={24} className="text-yellow-400/50 mb-1" />
        ) : (
          <>
            <div className="font-heading text-4xl sm:text-5xl text-yellow-400 leading-none" style={{ textShadow: '0 0 14px rgba(250,204,21,0.6)' }}>
              {String(index).padStart(2, '0')}
            </div>
            <div className="font-heading text-[10px] tracking-widest uppercase text-yellow-400/70 mt-1">Clue</div>
          </>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 p-4 sm:p-5 min-w-0">
        <h3 className={`font-heading text-lg sm:text-xl tracking-wide uppercase ${locked ? 'text-yellow-200/40' : 'text-yellow-100 group-hover:text-yellow-300'}`}>
          {locked ? 'Sealed' : clue.title}
        </h3>
        {!locked && clue.description && (
          <p className="font-sans text-sm text-yellow-100/70 mt-2 line-clamp-2">{clue.description}</p>
        )}
        {!locked && (clue.latitude || clue.longitude) && (
          <div className="flex items-center gap-1.5 mt-3 text-xs font-sans text-yellow-400/80">
            <MapPin size={13} />
            <span>{clue.latitude?.toFixed(5)}, {clue.longitude?.toFixed(5)}</span>
          </div>
        )}
      </div>

      {/* Preview image thumbnail */}
      {!locked && clue.image_base64 && (
        <div className="hidden sm:block w-32 shrink-0 relative overflow-hidden">
          <img src={`data:image/jpeg;base64,${clue.image_base64}`} alt={clue.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/60" />
        </div>
      )}
    </button>
  );
}

function ClueModal({ clue, onClose, hintContact, clueNumber }) {
  const mapUrl = clue.latitude && clue.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${clue.latitude},${clue.longitude}`
    : null;

  const buildHintUrl = () => {
    if (!hintContact) return null;
    const msg = `Hi! I'm on the Okinawa Treasure Hunt and I'd love a hint for Clue #${clueNumber}: "${clue.title}". Can you help?`;
    const encoded = encodeURIComponent(msg);
    const url = hintContact.trim();
    if (url.startsWith('sms:')) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}body=${encoded}`;
    }
    if (url.startsWith('mailto:')) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}subject=${encodeURIComponent(`Hint request · Clue #${clueNumber}`)}&body=${encoded}`;
    }
    // Messenger (m.me), WhatsApp (wa.me), generic http — all support ?text= for Messenger/WhatsApp; fall back to just opening the URL.
    if (/m\.me|messenger\.com|wa\.me|api\.whatsapp\.com/i.test(url)) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}text=${encoded}`;
    }
    // Instagram / generic — just open the profile; the user pastes the message.
    return url;
  };
  const hintUrl = buildHintUrl();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
      <div
        className="relative max-w-2xl w-full max-h-[92vh] overflow-y-auto rounded-2xl border border-yellow-700/40 bg-gradient-to-b from-[#1a1307] to-black shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {clue.image_base64 && (
          <div className="relative h-56 sm:h-72 overflow-hidden">
            <img src={`data:image/jpeg;base64,${clue.image_base64}`} alt={clue.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          </div>
        )}
        <div className="p-6 sm:p-8">
          <h2 className="font-heading text-3xl tracking-wide uppercase text-yellow-100 mb-3">{clue.title}</h2>
          {clue.description && (
            <p className="font-sans text-base text-yellow-100/90 leading-relaxed whitespace-pre-wrap">{clue.description}</p>
          )}

          {(clue.latitude || clue.longitude) && (
            <div className="mt-5 p-4 rounded-xl border border-yellow-700/40 bg-black/50">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-yellow-400">
                  <MapPin size={16} />
                  <span className="font-heading text-sm tracking-wide">COORDINATES</span>
                </div>
                <div className="font-sans text-sm text-yellow-100">
                  {clue.latitude?.toFixed(6)}, {clue.longitude?.toFixed(6)}
                </div>
              </div>
              {mapUrl && (
                <a
                  href={mapUrl} target="_blank" rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-heading tracking-widest uppercase bg-yellow-400 text-black px-4 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  <Compass size={14} /> Open in Google Maps
                </a>
              )}
            </div>
          )}

          {/* Ask for a hint button — opens a DM/SMS/email asking us for a hint */}
          {hintUrl ? (
            <a
              href={hintUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ask-hint-${clue.id}`}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-yellow-700/60 bg-yellow-950/30 hover:bg-yellow-900/40 hover:border-yellow-500 transition-all text-yellow-100 font-heading tracking-widest uppercase text-sm"
            >
              <MessageCircle size={16} className="text-yellow-400" />
              Stuck? Message us for a hint
            </a>
          ) : (
            <div className="mt-4 p-3 rounded-xl border border-yellow-900/40 bg-black/40 text-center">
              <p className="font-sans text-xs text-yellow-100/60 italic">Admin: set a Hint Contact URL in the Treasure Hunt settings to enable the "Ask for a hint" button.</p>
            </div>
          )}

          <button onClick={onClose} className="mt-4 w-full py-3 rounded-xl bg-yellow-900/30 hover:bg-yellow-800/40 text-yellow-100 font-heading tracking-widest uppercase text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
